import { useEffect, useState } from 'react';
import { JobProgress, jobProgress } from '../data/progress';

/**
 * The running clock for a job in progress, recomputed every second.
 *
 * ---------------------------------------------------------------------------
 * THE INTERVAL IS THE ONLY THING THIS ADDS
 * ---------------------------------------------------------------------------
 * All the arithmetic lives in `jobProgress`, which is pure and takes `now` as
 * an argument. This hook exists solely to supply a fresh `now` on a timer and
 * to tear that timer down again. Keeping the split means the hard part - what
 * "40 mins left" means when the estimate was a range - is tested without
 * faking a clock.
 *
 * ---------------------------------------------------------------------------
 * IT STOPS WHEN THERE IS NOTHING TO COUNT
 * ---------------------------------------------------------------------------
 * `active` is false on every stage but `service`, and the effect then never
 * schedules an interval at all. A timer left ticking behind a closed booking
 * is a wakeup a second, forever, on somebody's battery - and it would re-render
 * a screen nobody is looking at. The cleanup covers the ordinary unmount; the
 * `active` guard covers the much more common case of the panel simply not
 * being the one on screen.
 */
export const useJobProgress = (
  startedAt: string | undefined,
  duration: string | undefined,
  active: boolean,
): JobProgress | null => {
  /*
   * Seeded with a real reading rather than null, so the first paint already
   * shows the clock. Starting at null would flash the panel in empty for one
   * frame every time the booking is opened.
   */
  const [progress, setProgress] = useState<JobProgress | null>(() =>
    active ? jobProgress(startedAt, duration) : null,
  );

  useEffect(() => {
    if (!active) {
      setProgress(null);
      return;
    }

    /* Immediately, so reopening a booking does not wait a second for a
       reading taken before the props changed. */
    setProgress(jobProgress(startedAt, duration));

    const id = setInterval(() => {
      setProgress(jobProgress(startedAt, duration));
    }, 1000);

    return () => clearInterval(id);
  }, [startedAt, duration, active]);

  return progress;
};

export default useJobProgress;
